import { motion } from 'motion/react';
import { useCallback, useEffect } from 'react';

import { useNavigation } from '@hooks/useNavigation';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export const SuccessScreen = () => {
  const { navigate } = useNavigation();

  // Track page view for Google Ads conversion tracking (prevent double-counting)
  useEffect(() => {
    // Only track conversions on production (not localhost)
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (!isLocalhost && typeof window !== 'undefined' && window.gtag) {
      // Check if we've already tracked this conversion in this session
      const conversionTracked = sessionStorage.getItem(
        'yolo_conversion_tracked',
      );

      if (!conversionTracked) {
        // Send conversion event with your specific conversion label
        window.gtag('event', 'conversion', {
          send_to: 'AW-11326136924/USubCM-wk5YbENzE3Jgq',
          value: 10.0,
          currency: 'USD',
          event_category: 'engagement',
          event_label: 'form_submission',
        });

        // Mark conversion as tracked for this session
        sessionStorage.setItem('yolo_conversion_tracked', 'true');
      }
    } else if (isLocalhost) {
      console.log('Skipping conversion tracking on localhost');
    }
  }, []);

  const handleContinue = useCallback(() => {
    navigate('landing');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-dark text-white flex flex-col items-center justify-center px-6 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Success Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          type: 'spring',
          damping: 20,
          stiffness: 300,
          duration: 0.8,
        }}
        className="text-center space-y-8 max-w-md mx-auto"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            damping: 15,
            stiffness: 400,
            delay: 0.2,
          }}
          className="w-24 h-24 mx-auto mb-8"
        >
          <div className="w-full h-full rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-2xl">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-white"
            >
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                d="M20 6L9 17L4 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="space-y-4"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white">
            Success!
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Your form has been submitted successfully.
            <br />
            We'll be in touch soon!
          </p>
        </motion.div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="pt-8"
        >
          <button
            onClick={handleContinue}
            className="text-white hover:opacity-90 transition-all duration-200 hover:scale-105"
            style={{
              position: 'relative',
              cursor: 'pointer',
              lineHeight: '1',
              display: 'inline-flex',
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              verticalAlign: 'middle',
              textDecoration: 'none',
              overflow: 'hidden',
              transform: 'translateZ(0px)',
              outline: 'none',
              outlineOffset: '-1px',
              height: '48px',
              fontSize: '16px',
              padding: '0px 2em',
              gap: '0.5em',
              borderRadius: '12px',
              border: '1px solid #00FF40',
              background: 'rgba(65, 255, 65, 0.20)',
              boxShadow:
                '0 27px 80px 0 rgba(0, 0, 0, 0.07), 0 6.031px 17.869px 0 rgba(0, 0, 0, 0.04), 0 1.796px 5.32px 0 rgba(0, 0, 0, 0.03)',
            }}
          >
            Continue
          </button>
        </motion.div>
      </motion.div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-green-400/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: window.innerHeight + 20,
            }}
            animate={{
              y: -20,
              x: Math.random() * window.innerWidth,
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </div>
  );
};
